import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Terminal, ArrowRight, Award, ChevronLeft, TrendingUp, Quote } from "lucide-react";
import usePageview from "@/lib/usePageview";

// Phase 25.2b — Public case studies. Bloomberg-styled to mirror the rest of DARA.
// /case-studies         — index of all published
// /case-studies/{slug}  — detail page

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function CaseStudiesIndex() {
  usePageview("/case-studies");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/case-studies`)
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="case-studies-index">
      <header className="border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black tracking-tight text-lg">DARA</span>
          </Link>
          <Link to="/login" className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2">
            LOGIN
          </Link>
        </div>
      </header>

      <section className="border-b border-zinc-900 py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-4">
            // CUSTOMER STORIES
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Operators in production with DARA.
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mt-6 leading-relaxed">
            Real numbers, real outcomes. Each case study is approved + signed off
            by the operator before publication. Every metric is pulled directly
            from their live DARA workspace.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {loading && <div className="text-center font-mono text-[11px] uppercase tracking-widest text-zinc-600">LOADING…</div>}
          {!loading && items.length === 0 && (
            <div className="border border-zinc-900 bg-zinc-950/40 p-10 text-center max-w-2xl mx-auto">
              <Award className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <h3 className="font-heading font-bold text-lg">First case studies coming soon</h3>
              <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto leading-relaxed">
                We're working with our first wave of operators. If you're using DARA
                and want to be featured (with full editorial control), email
                <a href="mailto:hello@daraos.ai" className="text-[#FFB000] mx-1">hello@daraos.ai</a>.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 mt-6 font-mono text-[11px] uppercase tracking-widest text-[#FFB000] hover:underline">
                <ChevronLeft className="w-3 h-3" /> BACK TO HOMEPAGE
              </Link>
            </div>
          )}
          {!loading && items.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="cs-public-grid">
              {items.map(cs => (
                <Link
                  key={cs.slug}
                  to={`/case-studies/${cs.slug}`}
                  data-testid={`cs-public-${cs.slug}`}
                  className="border border-zinc-900 bg-zinc-950/40 p-5 hover:border-[#FFB000]/50 hover:bg-zinc-950 transition-colors group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000]">// {cs.industry || "CASE STUDY"}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-zinc-700 group-hover:text-[#FFB000]" />
                  </div>
                  <div className="font-heading font-black text-lg text-white tracking-tight mb-2">{cs.customer_name}</div>
                  <p className="text-sm text-zinc-400 line-clamp-3 leading-relaxed">{cs.headline}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export function CaseStudyDetail() {
  const { slug } = useParams();
  usePageview(`/case-studies/${slug}`);
  const [cs, setCs] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/case-studies/${slug}`)
      .then(r => setCs(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center font-mono text-[11px] uppercase tracking-widest text-zinc-600">LOADING…</div>;
  }
  if (notFound || !cs) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-32 text-center">
          <Award className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <h1 className="font-heading font-black text-3xl">Case study not found</h1>
          <p className="text-zinc-500 mt-3">This case study may not be published yet.</p>
          <Link to="/case-studies" className="inline-flex items-center gap-2 mt-6 font-mono text-[11px] uppercase tracking-widest text-[#FFB000] hover:underline">
            <ChevronLeft className="w-3 h-3" /> ALL CASE STUDIES
          </Link>
        </div>
      </div>
    );
  }

  const metrics = Object.values(cs.dara_metrics || {}).filter(m => m && m.label);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="case-study-detail">
      <header className="border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black tracking-tight text-lg">DARA</span>
          </Link>
          <Link to="/case-studies" className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1">
            <ChevronLeft className="w-3 h-3" /> ALL STORIES
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-zinc-900 py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-4">
            // {cs.industry || "CASE STUDY"} · CUSTOMER STORY
          </div>
          {cs.customer_logo_url && (
            <img src={cs.customer_logo_url} alt={cs.customer_name} className="h-12 mb-6 opacity-90" />
          )}
          <div className="font-heading font-black text-2xl tracking-tight text-zinc-400 mb-2">{cs.customer_name}</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] text-white">
            {cs.headline}
          </h1>
        </div>
      </section>

      {/* Metrics */}
      {metrics.length > 0 && (
        <section className="border-b border-zinc-900 py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className={`grid grid-cols-1 ${metrics.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"} gap-4`}>
              {metrics.map((m, i) => (
                <div key={i} className="border border-zinc-900 bg-gradient-to-br from-[#FFB000]/[0.04] to-zinc-950/40 p-6" data-testid={`cs-metric-${i}`}>
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-2">{m.label}</div>
                  <div className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-[#FFB000]">{m.value || "—"}</div>
                  {m.change_pct && (
                    <div className="font-mono text-[10px] text-[#00E676] mt-2 inline-flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> {m.change_pct}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Body */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-10">
          {cs.before_pain && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF6B47] mb-3">// BEFORE DARA</div>
              <p className="text-base text-zinc-300 leading-relaxed">{cs.before_pain}</p>
            </div>
          )}

          {cs.quote_body && (
            <div className="border-l-4 border-[#FFB000] bg-zinc-950/40 px-6 py-5">
              <Quote className="w-5 h-5 text-[#FFB000] mb-3" />
              <p className="text-lg sm:text-xl text-white leading-relaxed font-heading">&ldquo;{cs.quote_body}&rdquo;</p>
              {cs.quote_attribution && (
                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mt-4">— {cs.quote_attribution}</div>
              )}
            </div>
          )}

          {cs.outcome && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00E676] mb-3">// THE OUTCOME</div>
              <p className="text-base text-zinc-300 leading-relaxed">{cs.outcome}</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-zinc-900 py-16 bg-gradient-to-b from-zinc-950/30 to-black">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">
            {cs.cta_text || "See how DARA works for your business"}
          </h2>
          <Link
            to={cs.cta_url || "/"}
            data-testid="cs-cta-link"
            className="inline-flex items-center gap-2 mt-6 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-6 py-3 hover:opacity-90"
          >
            START FREE TRIAL <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
